import 'react-native-get-random-values'

import { debounce } from 'lodash'
import { observer } from 'mobx-react-lite'
import React from 'react'
import { Alert, ImageBackground, Platform, Pressable, ScrollView, StyleSheet, Text, View } from 'react-native'
import { SafeAreaView } from 'react-native-safe-area-context'
import { Button, Divider } from 'react-native-paper'

import image from '../../../assets/Admin/pokerpower-watermark.png'
import { MainHeader } from '../../components/MainHeader/MainHeader'
import { useStore } from '../../models'
import { getAppVersion, getBuildNumber } from '../../utils/appVersion'
// import { AdminRoutes } from '../../navigation/admin-navigator'
// import { DrawerRoutes } from '../../navigation/drawer-navigator'
// import { HomeNavigation, HomeRoutes } from '../../navigation/homeNavigator'
import styles from '../../styles/globalStyle'

import { crashJavascriptThread, crashNativeMainThread } from 'react-native-performance-limiter'
import { DS } from '../../design-system'
import { useRemoteConfig } from '../../../module/remote-config/useRemoteConfig'
import { Route, useRouter } from 'expo-router'
import { AntDesign, MaterialIcons } from '@expo/vector-icons'

// const crashApp = () => {
//   Alert.prompt('Crash Message', 'Set an error message for the crash', [
//     {
//       text: 'CRASH: Native',
//       isPreferred: true,
//       onPress(value) {
//         const msg = value?.toString() || 'App crashed - error message'
//         crashNativeMainThread(`TEST: ${msg}`)
//       },
//       style: 'destructive'
//     },
//     {
//       text: 'CRASH: JS',
//       onPress(value) {
//         const msg = value?.toString() || 'App crashed - error message'
//         crashJavascriptThread(`TEST: ${msg}`)
//       },
//       style: 'default'
//     },
//     {
//       text: 'Cancel',
//       onPress() {
//         //
//       },
//       style: 'cancel'
//     }
//   ])
// }

const startStyles = StyleSheet.create({
  container: {
    flex: 1
  },
  image: {
    flex: 1
  },
  text: {
    color: 'white',
    fontSize: 42,
    fontWeight: 'bold',
    textAlign: 'center',
    backgroundColor: '#000000a0'
  }
})

export const AdminScreen = observer(() => {
  const router = useRouter()
  const store = useStore()
  const versionAndBuild = `v${getAppVersion()} (${getBuildNumber()}) ${store.environment.stage.toUpperCase()}`

  const closeAdminPanel = () => {
    if (store.game.game?.gameId) {
      router.navigate('/game')
      return
    }

    router.navigate('/(root)/(tabs)')
  }

  const onPressNavigateTo = () => {
    Alert.prompt(
      'Enter the route',
      'Should be a valid route starting with /',
      [
        {
          text: 'Cancel',
          onPress: () => console.log('Cancel Pressed'),
          style: 'cancel'
        },
        {
          text: 'OK',
          onPress: (value?: string | undefined) => {
            if (value) {
              router.navigate(value as Route)
            } else {
              Alert.alert('Error', 'Route cannot be empty.')
            }
          }
        }
      ],
      'plain-text',
      '/' // Optional: Default value for the input field
    )
  }

  return (
    <ImageBackground resizeMode="cover" source={image} style={startStyles.image}>
      <SafeAreaView style={styles.container}>
        <View style={{ paddingHorizontal: 16 }}>
          <View style={{ flexDirection: 'row', justifyContent: 'space-between' }}>
            <View style={{ flexDirection: 'row', gap: 8, justifyContent: 'center' }}>
              <MaterialIcons name="admin-panel-settings" size={24} color="black" />
              <DS.Text variant="Heading 3/Regular">Admin Panel</DS.Text>
            </View>
            <Pressable onPress={closeAdminPanel} hitSlop={10}>
              <AntDesign name="close" size={24} color="black" />
            </Pressable>
          </View>
        </View>

        <ScrollView
          style={{
            flexGrow: 1
          }}
          contentContainerStyle={{
            gap: 16,
            padding: 16
          }}
        >
          <View style={{ alignItems: 'center', marginTop: 16 }}>
            <View
              style={{
                alignItems: 'center',
                backgroundColor: '#F6F2E9',
                borderColor: '#F6D2E9',
                borderWidth: 1,
                paddingVertical: 4,
                paddingHorizontal: 12,
                borderRadius: 5
              }}
            >
              <Text style={{ fontSize: 14 }}>{versionAndBuild}</Text>
            </View>
          </View>
          <Button
            key="account-force-refresh"
            onPress={debounce(() => {
              store.messages.unsubscribeGraphQLSubscription(true)
              closeAdminPanel()
            }, 250)}
            icon="cached"
            mode="contained-tonal"
          >
            Force Refresh
          </Button>
          <Button
            key="clear-onboarding"
            onPress={debounce(() => {
              store.session.clearUserInfo()
              store.session.setIsLoggedInFirstTime(true)
              closeAdminPanel()
            }, 250)}
            icon="cancel"
            mode="contained-tonal"
          >
            Clear Onboarding
          </Button>

          <Button
            key="account-force-clear-modals"
            onPress={debounce(() => {
              store.game.clearGameModalQueue()
              closeAdminPanel()
            }, 250)}
            icon="notification-clear-all"
            mode="contained-tonal"
          >
            Clear Modal Queue
          </Button>

          <Divider />

          <Button
            disabled={store.communication.channel === undefined}
            key="account-group"
            onPress={debounce(() => {
              router.navigate('/(root)/admin/channels')
            }, 250)}
            icon="account-group"
            mode="contained-tonal"
          >
            Channels
          </Button>

          <Button
            key="dashboard"
            onPress={debounce(() => {
              router.navigate('/(admin)/dashboard')
            }, 250)}
            icon="view-dashboard"
            mode="contained-tonal"
            disabled={Platform.select({ web: false, default: true })}
          >
            Dashboard
          </Button>
          <Button
            key="reports"
            onPress={debounce(() => {
              router.navigate('/(admin)/reports')
            }, 250)}
            icon="chart-bar"
            mode="contained-tonal"
            disabled={Platform.select({ web: false, default: true })}
          >
            Reports
          </Button>
          <Button
            key="env"
            onPress={debounce(() => {
              router.navigate('/(root)/admin/environment')
            }, 250)}
            icon="earth"
            mode="contained-tonal"
          >
            Environment
          </Button>
          <Button
            key="feature-flags"
            onPress={debounce(() => {
              router.navigate('/(root)/admin/feature-flags')
            }, 250)}
            icon="flag"
            mode="contained-tonal"
          >
            Feature Flags
          </Button>
          <Button
            key="stress-test"
            onPress={debounce(() => {
              router.navigate('/(root)/admin/stress-test')
            }, 250)}
            icon="speedometer"
            mode="contained-tonal"
          >
            Stress Test
          </Button>
          <Button
            key="sidepot-test"
            onPress={debounce(() => {
              router.navigate('/(root)/admin/sidepot-test')
            }, 250)}
            icon="layers-triple"
            mode="contained-tonal"
          >
            Automatic Testing
          </Button>
          <Button
            key="badges"
            onPress={debounce(() => {
              router.navigate('/(root)/admin/badges')
            }, 250)}
            icon="medal"
            mode="contained-tonal"
          >
            Badges
          </Button>

          {/* <Button
            key="cards-playing-outline"
            onPress={debounce(() => {
              navigation.navigate(AdminRoutes.games)
            }, 250)}
            icon="cards-playing-outline"
            textColor="#121212"
            mode="contained-tonal"
          >
            Games
          </Button> */}

          <Button
            key="message-alert-outline"
            onPress={debounce(() => {
              router.navigate('/(root)/admin/messages')
            }, 250)}
            icon="message-alert-outline"
            mode="contained-tonal"
          >
            Messages
          </Button>

          <Button
            key="network"
            onPress={debounce(() => {
              router.navigate('/(root)/admin/network')
            }, 250)}
            icon="access-point"
            mode="contained-tonal"
          >
            Network
          </Button>

          <Button
            key="scheduled-games"
            onPress={debounce(() => {
              router.navigate('/(root)/admin/scheduled-games')
            }, 250)}
            icon="calendar-clock"
            mode="contained-tonal"
          >
            Scheduled Games
          </Button>

          <Button
            key="navigateTo"
            onPress={debounce(() => {
              onPressNavigateTo()
            }, 250)}
            icon="navigation-variant"
            mode="contained-tonal"
          >
            Navigate To
          </Button>

          {/* <Button
            key="crash"
            onPress={debounce(() => {
              crashApp()
            }, 250)}
            icon="bomb"
            textColor="#Fff"
            mode="contained-tonal"
            buttonColor="#f06"
          >
            Crash App
          </Button> */}

          <Divider />

          <Button
            key="exit-to-app"
            onPress={debounce(() => {
              closeAdminPanel()
            }, 250)}
            icon="exit-to-app"
            mode="contained"
          >
            Leave Admin
          </Button>
        </ScrollView>
        <View style={{ alignItems: 'flex-end', marginRight: 16 }}>
          <Text style={{ fontSize: 14 }}>{versionAndBuild}</Text>
        </View>
      </SafeAreaView>
    </ImageBackground>
  )
})
